import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { UserService } from '@/services/users/UserService';
import { AgentCacheManager } from '@/services/agent/core/AgentCacheManager';

interface AgentCreatedPayload {
  agentId: string;
  elevenLabsId: string;
  agentName: string;
  userId: string;
  timestamp: string;
}

export const useAgentCreationNotifications = () => {
  const queryClient = useQueryClient();

  const getCurrentUserId = useCallback(async (): Promise<string | null> => {
    try {
      const { data: { user }, error: userError } = await UserService.getUser();
      if (userError || !user?.id) return null;
      return user.id;
    } catch (error) {
      console.error('Error getting current user for agent creation notifications:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setupNotificationListener = async () => {
      try {
        const userId = await getCurrentUserId();

        if (!isMounted) return;

        if (!userId) {
          console.warn('No user ID available for agent creation notifications');
          return;
        }

        console.log(`[AgentNotifications] Setting up listener for userId: ${userId}`);
        channel = supabase.channel('agent-creation');

        channel
          .on(
            'broadcast',
            { event: 'agent_created' },
            ({ payload }: { payload: AgentCreatedPayload }) => {
              // Only handle notifications for the current user
              if (payload.userId !== userId) return;

              console.log('[AgentNotifications] Handler fired - Received agent_created event:', payload);

              toast.success(
                `Genie "${payload.agentName}" created successfully!`,
                {
                  description: 'Your new genie is ready to use.',
                  duration: 5000,
                  action: {
                    label: 'View',
                    onClick: () => {
                      window.location.href = '/genie-hub/my-genies';
                    },
                  },
                }
              );

              // Invalidate agent queries to refresh the list
              AgentCacheManager.invalidateCache();
              queryClient.invalidateQueries({ queryKey: ['agents'] });
              queryClient.invalidateQueries({ queryKey: ['user-agents'] });
              queryClient.invalidateQueries({ queryKey: ['agents-list'] });
              queryClient.invalidateQueries({ queryKey: ['all-agents'] });
            }
          )
          .subscribe((status) => {
            if (status === 'SUBSCRIBED') {
              console.log(`[AgentNotifications] Subscribed to agent-creation channel`);
            } else if (status === 'CLOSED') {
              console.log(`[AgentNotifications] Unsubscribed from agent-creation channel`);
            }
          });
      } catch (error) {
        console.error('Error setting up agent creation notification listener:', error);
      }
    };

    setupNotificationListener();

    return () => {
      isMounted = false;
      if (channel) {
        channel.unsubscribe();
      }
    };
  }, [queryClient, getCurrentUserId]);
};
